import i18n, { type BackendModule } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { bundledResources, supportedLanguages, translationLoaders } from './locales';
import { DEFAULT_LANGUAGE, RTL_LANGUAGES, splitLanguageFromPath } from './lib/localeRoutes';

// The URL is the source of truth for language: /de/rooms is German for everyone,
// crawler or not. Without this, a visitor's cookie could serve German at an
// English URL, which desyncs the page from its own canonical/hreflang tags.
const pathDetector = {
  name: 'localePath',
  lookup() {
    if (typeof window === 'undefined') return undefined;
    const { lang } = splitLanguageFromPath(window.location.pathname);
    // Only report a *prefixed* language. At an un-prefixed path we return
    // undefined so the cookie can still express a returning visitor's choice,
    // which LanguageRouter then turns into a redirect to the prefixed URL.
    return lang === DEFAULT_LANGUAGE ? undefined : lang;
  },
};

const detector = new LanguageDetector();
detector.addDetector(pathDetector);

// Fetches a non-English translation chunk the first time that language is
// needed. i18next only emits `languageChanged` once it has loaded, so a
// language switch never renders untranslated keys.
const lazyTranslations: BackendModule = {
  type: 'backend',
  init() {},
  read(language, _namespace, callback) {
    const load = translationLoaders[language];
    if (!load) {
      callback(null, {});
      return;
    }
    load().then(
      (data) => callback(null, data),
      (error) => callback(error, null),
    );
  },
};

// Resolves once the detected language's translations are available. main.tsx
// waits for this before the first render, so /fr/... never flashes English.
export const i18nReady = i18n
  .use(lazyTranslations)
  .use(detector) // Detect user language
  .use(initReactI18next) // Pass i18n instance to react-i18next
  .init({
    resources: bundledResources,
    // English is bundled; everything else comes from lazyTranslations.
    partialBundledLanguages: true,
    supportedLngs: [...supportedLanguages],
    fallbackLng: 'en', // English as default/fallback
    debug: false,
    react: {
      // Resources are always loaded before render (see i18nReady), so there's
      // nothing to suspend on — and suspending would blank the page on switch.
      useSuspense: false,
    },

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    detection: {
      // URL prefix wins, then explicit user choice, then site default ("en").
      order: ['localePath', 'cookie', 'htmlTag'],
      // Keys to lookup language from
      lookupCookie: 'i18nextLng',
      // Cache user language in cookie
      caches: ['cookie'],
      // Cookie options
      cookieMinutes: 525600, // 1 year
      cookieDomain: window.location.hostname,
    },
  });

const applyDocumentLanguage = (lng: string) => {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lng;
  document.documentElement.dir = RTL_LANGUAGES.includes(lng) ? 'rtl' : 'ltr';
};

i18n.on('languageChanged', applyDocumentLanguage);

// Init is async now (non-English languages load on demand), so the language
// may not be known yet; the prerendered HTML already carries the right
// lang/dir, and the languageChanged handler above applies it once resolved.
if (i18n.language) applyDocumentLanguage(i18n.language);

export default i18n;
